/**
 * Service-worker half of the media account boundary (AUTHZ-02 E2). The invariant it serves is
 * stated in `mediaAccountBoundary.ts`: a media entry cached under an unsigned key must not
 * survive a change of user.
 *
 * Deleting the caches is not enough on its own. A StaleWhileRevalidate or CacheFirst write that
 * started for the previous user — the request left with that user's cookie — can finish after
 * the page has deleted `image-cache` / `media-uploads-cache`, and would then re-create exactly
 * the entry the purge removed. So the worker keeps a generation number, the page bumps it
 * (`BUMP_MEDIA_GENERATION`, answered with `MEDIA_GENERATION_BUMPED` on the message port) and
 * deletes only after the answer, and `plugin`, installed on both media routes, holds every write
 * to the generation its request left under:
 * - `requestWillFetch` records the generation as the request goes to the network;
 * - `cacheWillUpdate` refuses to cache the response once the generation has moved on;
 * - `cacheDidUpdate` deletes the entry it just wrote if the bump landed between that check and
 *   the put (Workbox awaits `caches.open` in between), so no interleaving lets a stale write
 *   through. A write with no recorded generation is refused: unknown is not "current".
 *
 * The in-flight guarantee lives in memory, within one worker lifetime. The value is also stored
 * in IndexedDB, because the browser stops and restarts the worker at will and the sequence
 * should continue rather than start again at 0. Every storage step is bounded by
 * `storageTimeoutMs`: IndexedDB can hang in a worker, and a hung read here would hang every
 * media request behind `requestWillFetch`. On timeout the worker carries on from memory.
 *
 * Dependency-free on purpose: the service worker imports this module.
 */

export const BUMP_MEDIA_GENERATION = 'BUMP_MEDIA_GENERATION'
export const MEDIA_GENERATION_BUMPED = 'MEDIA_GENERATION_BUMPED'

const DB_NAME = 'media-cache-generation'
const STORE_NAME = 'generation'
const STORE_KEY = 'current'
const STATE_KEY = 'mediaCacheGeneration'
const DEFAULT_STORAGE_TIMEOUT_MS = 1000

type PluginState = Record<string, unknown>

export type MediaCacheGenerationPlugin = {
  requestWillFetch: (param: { request: Request; state?: PluginState }) => Promise<Request>
  cacheWillUpdate: (param: { response: Response; state?: PluginState }) => Promise<Response | null>
  cacheDidUpdate: (param: {
    cacheName: string
    request: Request
    state?: PluginState
  }) => Promise<void>
}

export type MediaCacheGeneration = {
  /** The generation now in force (waits, bounded, for the stored value on first use). */
  current: () => Promise<number>
  /** Moves to the next generation; resolves with it once storing it was attempted. */
  bump: () => Promise<number>
  plugin: MediaCacheGenerationPlugin
}

export function createMediaCacheGeneration(
  options: { indexedDB?: IDBFactory; storageTimeoutMs?: number } = {},
): MediaCacheGeneration {
  const { indexedDB: idb, storageTimeoutMs = DEFAULT_STORAGE_TIMEOUT_MS } = options
  let generation = 0

  const ready: Promise<void> = idb
    ? within(readStored(idb), storageTimeoutMs).then(
        stored => {
          if (stored !== undefined && stored > generation) generation = stored
        },
        () => undefined,
      )
    : Promise.resolve()

  // Writes are chained so the store never ends on an older value than memory.
  let persisted: Promise<void> = Promise.resolve()

  async function current(): Promise<number> {
    await ready
    return generation
  }

  async function bump(): Promise<number> {
    await ready
    generation += 1
    const value = generation
    if (idb) {
      persisted = persisted.then(() =>
        within(writeStored(idb, generation), storageTimeoutMs).catch(() => undefined),
      )
      await persisted
    }
    return value
  }

  async function isCurrent(state: PluginState | undefined): Promise<boolean> {
    const started = state?.[STATE_KEY]
    return typeof started === 'number' && started === (await current())
  }

  const plugin: MediaCacheGenerationPlugin = {
    requestWillFetch: async ({ request, state }) => {
      if (state) state[STATE_KEY] = await current()
      return request
    },
    cacheWillUpdate: async ({ response, state }) => ((await isCurrent(state)) ? response : null),
    cacheDidUpdate: async ({ cacheName, request, state }) => {
      if (await isCurrent(state)) return
      try {
        await (await caches.open(cacheName)).delete(request)
      } catch {
        // The purge that bumped the generation deletes the whole cache anyway.
      }
    },
  }

  return { current, bump, plugin }
}

function within<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`storage did not answer within ${ms} ms`)), ms)
    promise.then(
      value => {
        clearTimeout(timer)
        resolve(value)
      },
      error => {
        clearTimeout(timer)
        reject(error)
      },
    )
  })
}

function openStore(idb: IDBFactory): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = idb.open(DB_NAME, 1)
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE_NAME)
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
    request.onblocked = () => reject(new Error(`${DB_NAME} is blocked`))
  })
}

async function readStored(idb: IDBFactory): Promise<number | undefined> {
  const db = await openStore(idb)
  try {
    return await new Promise((resolve, reject) => {
      const request = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(STORE_KEY)
      request.onsuccess = () =>
        resolve(typeof request.result === 'number' ? request.result : undefined)
      request.onerror = () => reject(request.error)
    })
  } finally {
    db.close()
  }
}

async function writeStored(idb: IDBFactory, value: number): Promise<void> {
  const db = await openStore(idb)
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite')
      transaction.objectStore(STORE_NAME).put(value, STORE_KEY)
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => reject(transaction.error)
      transaction.onabort = () => reject(transaction.error)
    })
  } finally {
    db.close()
  }
}
